import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Instagram, Mail, MapPin, Clock, ChevronRight, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import '../styles/home.css';

const Home = () => {
  const navigate = useNavigate();

  const galleryImages = [
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/kg5m378f_20251223_173633_0000.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/qyckf7et_Design%20sans%20titre_20251222_132610_0000.jpg',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/uyr1cum2_file_000000000ca071f5b9e6382d81d2aadf.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/spoggssq_file_00000000390871f59dca04bc559ab3e0.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/08m2v8t4_file_0000000080d87246bdfb366f5177bdc3.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/szszrbns_file_000000007b6c71f4867018321eedc93e.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/ji3djrd5_file_00000000086071f49bad99f352e2fb0a.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/eiwvmnee_file_0000000085b871f48102d024e649908d.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/u0nzqdln_file_00000000516471f48fe250b821fdfbc0.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/duyuqhns_file_000000000880722fbd9fc4fad9a22634.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/mscuuukg_file_00000000b65c720a8813987cb86f54b8.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/qwfabl0v_file_00000000545c722faf181fb23488ecd2.png',
    'https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/y79hxg3r_file_00000000fad4722fbac0c3e9d32553ff.png'
  ];

  const prestations = [
    {
      title: 'Événements d\'Entreprise',
      description: 'Séminaires, team building, repas d\'entreprise - Pizza Mamma apporte la convivialité italienne à vos événements professionnels.',
      icon: '🏢'
    },
    {
      title: 'Mariages',
      description: 'Célébrez votre union avec nos pizzas artisanales cuites au feu de bois. Une expérience culinaire mémorable pour vos invités.',
      icon: '💒'
    },
    {
      title: 'Anniversaires',
      description: 'Fêtes d\'anniversaire inoubliables avec nos pizzas authentiques. Parfait pour tous les âges et tous les goûts.',
      icon: '🎂'
    },
    {
      title: 'Festivals',
      description: 'Présence sur festivals et événements publics. Nous apportons la tradition italienne partout où vous nous invitez.',
      icon: '🎪'
    }
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-container">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="nav-container">
          <div className="logo-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_49df62c5-2f2b-4015-9d48-0abd42138eec/artifacts/jxvxk1uq_file_00000000b8f0720a897da348f1691752.png" 
              alt="Pizza Mamma Logo" 
              className="logo"
            />
            <div className="brand-name">
              <span className="brand-pizza">PIZZA</span>
              <span className="brand-mamma">MAMMA</span>
            </div>
          </div>
          <div className="nav-links">
            <button onClick={() => scrollToSection('galerie')} className="nav-link">Galerie</button>
            <button onClick={() => scrollToSection('prestations')} className="nav-link">Nos Prestations</button>
            <a 
              href="https://wa.me/33613658698" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-link whatsapp-link"
            >
              <MessageCircle size={20} />
              <span>WhatsApp</span>
            </a>
            <button onClick={() => navigate('/demande-devis')} className="nav-link nav-link-cta">Demande de Devis</button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="hero-title-main">Pizza napolitaine au feu de bois & privatisation d'événements</span>
          </h1>
          <div className="pizza-hero-image">
            <img 
              src="https://customer-assets-0z36b82j.emergentagent.net/job_28695a98-47a2-4e41-bd0e-bcfec322e88c/artifacts/lu9xx5ls_file_0000000016f47243885eaa2ffacb4f55.png" 
              alt="Pizza Napolitaine au Feu de Bois"
              className="pizza-image"
            />
          </div>
          <p className="hero-description">
            Commandez une authentique pizza napolitaine à Créteil ou privatisez notre food truck pour vos événements d'entreprise, mariages et anniversaires partout en Île-de-France.
          </p>
          <div className="hero-buttons-dual">
            <a href="tel:0613658698" className="btn-hero-option btn-order">
              <div className="btn-icon">🍕</div>
              <div className="btn-content">
                <div className="btn-title">Commander une pizza</div>
                <div className="btn-subtitle">Commande par téléphone au 06 13 65 86 98</div>
              </div>
            </a>
            <button onClick={() => navigate('/demande-devis')} className="btn-hero-option btn-quote">
              <div className="btn-icon">🏢</div>
              <div className="btn-content">
                <div className="btn-title">Demander un devis</div>
                <div className="btn-subtitle">Privatisation pour entreprises et événements privés</div>
              </div>
            </button>
          </div>
          
          {/* Location & Hours - Moved here */}
          <section className="info-banner-hero">
            <div className="info-item">
              <MapPin className="info-icon" size={24} />
              <div>
                <div className="info-label">Notre Emplacement</div>
                <a 
                  href="https://waze.com/ul?q=Place+des+alizés+Créteil" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="info-value info-link"
                >
                  Place des Alizés, Créteil
                </a>
              </div>
            </div>
            <div className="info-item">
              <Clock className="info-icon" size={24} />
              <div>
                <div className="info-label">Horaires d'ouverture</div>
                <div className="info-value">Tous les soirs (sauf vendredi) · 18h - 22h</div>
              </div>
            </div>
            <div className="info-contacts">
              <a href="tel:0613658698" className="contact-icon">
                <Phone size={20} />
              </a>
              <a href="https://instagram.com/pizzamamma94" target="_blank" rel="noopener noreferrer" className="contact-icon">
                <Instagram size={20} />
              </a>
              <a href="mailto:maarekpaul@gmail.com" className="contact-icon">
                <Mail size={20} />
              </a>
            </div>
          </section>
        </div>
        <div className="hero-image">
          <img 
            src="https://customer-assets.emergentagent.com/job_mamma-street-food/artifacts/6yip3gnn_file_000000002c10720a9d5accf9614258d3.png" 
            alt="Pizza Mamma Food Truck"
            className="hero-truck-image"
          />
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galerie" className="gallery-section">
        <div className="section-header">
          <h2 className="section-title">Notre Galerie</h2>
          <p className="section-subtitle">Découvrez nos créations artisanales</p>
        </div>
        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <div key={index} className="gallery-item">
              <img src={image} alt={`Pizza ${index + 1}`} className="gallery-image" />
            </div>
          ))}
        </div>
      </section>

      {/* Prestations Section */}
      <section id="prestations" className="prestations-section">
        <div className="section-header">
          <h2 className="section-title">Nos Prestations</h2>
          <p className="section-subtitle">Pizza Mamma s'adapte à tous vos événements</p>
        </div>
        <div className="prestations-grid">
          {prestations.map((prestation, index) => (
            <Card key={index} className="prestation-card">
              <CardContent className="prestation-content">
                <div className="prestation-icon">{prestation.icon}</div>
                <h3 className="prestation-title">{prestation.title}</h3>
                <p className="prestation-description">{prestation.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section - Demande de Devis */}
      <section id="devis" className="devis-cta-section">
        <div className="devis-cta-container">
          <div className="devis-cta-content">
            <h2 className="section-title">Prêt à Organiser Votre Événement ?</h2>
            <p className="section-subtitle">
              Demandez votre devis personnalisé en quelques clics. 
              Nous vous recontacterons rapidement pour discuter de votre projet.
            </p>
            <Button onClick={() => navigate('/demande-devis')} className="btn-cta-large">
              Demander un Devis Gratuit
              <ChevronRight className="ml-2" size={24} />
            </Button>
            <div className="cta-trust-badges">
              <div className="trust-badge">✅ Réponse sous 24-48h</div>
              <div className="trust-badge">✅ Devis personnalisé</div>
              <div className="trust-badge">✅ Sans engagement</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_49df62c5-2f2b-4015-9d48-0abd42138eec/artifacts/jxvxk1uq_file_00000000b8f0720a897da348f1691752.png" 
                alt="Pizza Mamma" 
                className="footer-logo-img"
              />
              <div className="footer-brand">
                <span className="footer-pizza">PIZZA</span>
                <span className="footer-mamma">MAMMA</span>
              </div>
            </div>
            <p className="footer-tagline">L'authenticité italienne à Créteil</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-title">Contact</h4>
            <div className="footer-links">
              <a href="tel:0613658698" className="footer-link">
                <Phone size={16} />
                <span>06 13 65 86 98</span>
              </a>
              <a href="mailto:maarekpaul@gmail.com" className="footer-link">
                <Mail size={16} />
                <span>maarekpaul@gmail.com</span>
              </a>
              <a href="https://instagram.com/pizzamamma94" target="_blank" rel="noopener noreferrer" className="footer-link">
                <Instagram size={16} />
                <span>@pizzamamma94</span>
              </a>
            </div>
          </div>
          <div className="footer-section">
            <h4 className="footer-title">Localisation</h4>
            <div className="footer-links">
              <a href="https://waze.com/ul?q=Place+des+alizés+Créteil" target="_blank" rel="noopener noreferrer" className="footer-link">
                <MapPin size={16} />
                <span>Place des Alizés, Créteil</span>
              </a>
              <div className="footer-link">
                <Clock size={16} />
                <span>Tous les soirs (sauf vendredi)<br/>18h - 22h</span>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 Pizza Mamma. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
